import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, FolderOpen, Zap } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function Hero({ className = '' }: { className?: string }) {
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const subheadlineRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const screenshotRef = useRef<HTMLDivElement>(null);
  const badgesRef = useRef<HTMLDivElement>(null);
  const pillsRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      const loadTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      loadTl.fromTo(headlineRef.current, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8 }, 0);
      loadTl.fromTo(subheadlineRef.current, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 }, 0.2);
      loadTl.fromTo(ctaRef.current, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5 }, 0.4);
      loadTl.fromTo(pillsRef.current, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5 }, 0.5);
      loadTl.fromTo(screenshotRef.current, { y: 60, scale: 0.95, opacity: 0 }, { y: 0, scale: 1, opacity: 1, duration: 1, ease: 'power2.out' }, 0.3);

      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: '+=70%',
          pin: true,
          scrub: 0.6,
          onLeaveBack: () => {
            gsap.set([headlineRef.current, subheadlineRef.current, ctaRef.current, pillsRef.current], { y: 0, opacity: 1 });
            gsap.set(screenshotRef.current, { y: 0, scale: 1, opacity: 1 });
          },
        },
      });
      scrollTl.fromTo(screenshotRef.current, { y: 0 }, { y: '-2vh', ease: 'none' }, 0);
      scrollTl.fromTo(headlineRef.current, { y: 0, opacity: 1 }, { y: '-8vh', opacity: 0, ease: 'power2.in' }, 0.7);
      scrollTl.fromTo(subheadlineRef.current, { y: 0, opacity: 1 }, { y: '-6vh', opacity: 0, ease: 'power2.in' }, 0.72);
      scrollTl.fromTo(ctaRef.current, { y: 0, opacity: 1 }, { y: '-4vh', opacity: 0, ease: 'power2.in' }, 0.74);
      scrollTl.fromTo(pillsRef.current, { opacity: 1 }, { opacity: 0, ease: 'power2.in' }, 0.75);
      scrollTl.fromTo(screenshotRef.current, { y: '-2vh', scale: 1, opacity: 1 }, { y: '10vh', scale: 1.02, opacity: 0.3, ease: 'power2.in' }, 0.7);
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className={`pinned-section bg-[#0B0F10] flex flex-col items-center justify-center pt-20 ${className}`}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(46, 196, 182, 0.08) 0%, transparent 50%)' }} />
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <div ref={badgesRef} className="inline-flex items-center gap-2 px-4 py-2 bg-[#2EC4B6]/10 border border-[#2EC4B6]/30 rounded-full mb-6">
          <Sparkles size={16} className="text-[#2EC4B6]" />
          <span className="text-sm text-[#2EC4B6] font-medium">AI-Powered Flyer Builder</span>
        </div>
        <div ref={headlineRef} className="mb-4">
          <h1 className="font-display font-bold text-[clamp(40px,6vw,80px)] leading-[1.05] tracking-[-0.03em] text-white">
            Your assets.<br />
            <span className="text-[#2EC4B6]">Always ready.</span>
          </h1>
        </div>
        <p ref={subheadlineRef} className="text-[clamp(16px,1.3vw,20px)] leading-relaxed text-[#A9B4B8] max-w-2xl mx-auto mb-6">
          Stop collecting files every time you need a flyer. Event.Tools keeps all your artist logos, venue assets, and brand elements organized—so AI generation always uses the right context.
        </p>
        <div ref={ctaRef} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <Link to="/signin" className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF6B4A] text-white font-medium rounded-lg hover:brightness-110 transition-all">
            Get early access
            <ArrowRight size={18} />
          </Link>
          <a href="#workflow" className="text-sm font-medium text-[#A9B4B8] hover:text-white transition-colors flex items-center gap-2">
            See how it works
          </a>
        </div>
        <div ref={pillsRef} className="flex flex-wrap items-center justify-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full text-sm text-[#A9B4B8]">
            <FolderOpen size={14} className="text-[#2EC4B6]" />
            Asset Library
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full text-sm text-[#A9B4B8]">
            <Zap size={14} className="text-[#2EC4B6]" />
            One-Click Generate
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full text-sm text-[#A9B4B8]">
            <Sparkles size={14} className="text-[#2EC4B6]" />
            Context-Aware AI
          </div>
        </div>
      </div>
      <div ref={screenshotRef} className="relative z-10 mt-8 px-6 w-full max-w-6xl" style={{ willChange: 'transform, opacity' }}>
        <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#2EC4B6]/20 via-transparent to-[#FF6B4A]/20 blur-xl opacity-50" />
          <img src="/screenshot-library.png" alt="Event.Tools Asset Library" className="relative w-full rounded-xl" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F10] via-transparent to-transparent opacity-60" />
        </div>
      </div>
    </section>
  );
}
